import { InputHTMLAttributes, forwardRef, ReactNode } from 'react';

/**
 * Input component props
 */
interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  size?: 'md' | 'sm';
}

/**
 * Input Component
 * Reusable form input with label, error handling, and icons
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      size = 'md',
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const isSm = size === 'sm';

    const baseInputClasses = isSm
      ? 'h-8 px-3 py-1.5 text-[12.5px] leading-tight border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-text-primary placeholder:text-text-muted'
      : 'px-4 py-2 border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const errorClasses = error
      ? 'border-red-500 focus:ring-red-500/30 focus:border-red-500'
      : isSm
        ? 'border-border focus:ring-brand/25 focus:border-border-focus'
        : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500';

    const iconPadding = leftIcon ? (isSm ? 'pl-9' : 'pl-10') : rightIcon ? (isSm ? 'pr-9' : 'pr-10') : '';

    const widthClass = fullWidth ? 'w-full' : '';

    const inputClasses = `${baseInputClasses} ${errorClasses} ${iconPadding} ${widthClass} ${className}`;

    const labelClasses = isSm
      ? 'block text-[11px] font-semibold text-text-muted mb-1 leading-snug'
      : 'block text-sm font-medium text-gray-700 mb-1';

    const messageClasses = isSm ? 'mt-1 text-[11px]' : 'mt-1 text-sm';

    return (
      <div className={`${fullWidth ? 'w-full' : ''}`}>
        {label && (
          <label className={labelClasses}>
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            className={inputClasses}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={error ? `${props.id}-error` : undefined}
            {...props}
          />

          {rightIcon && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              {rightIcon}
            </div>
          )}
        </div>

        {error && (
          <p className={`${messageClasses} text-red-600`} id={`${props.id}-error`}>
            {error}
          </p>
        )}

        {helperText && !error && (
          <p className={`${messageClasses} text-text-muted`}>{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
